import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './EmailVerification.css';

// Helper function to get backend URL
const getBackendUrl = () => {
  if (process.env.REACT_APP_BACKEND_URL) {
    return process.env.REACT_APP_BACKEND_URL;
  }
  const port = process.env.REACT_APP_SERVER_PORT || 4000;
  return `http://localhost:${port}`;
};

const EmailVerification = () => {
  const [email, setEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [step, setStep] = useState('verify'); // 'verify' or 'resend'
  
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get email from location state if available and auto-send verification code
  React.useEffect(() => {
    if (location.state?.email) {
      setEmail(location.state.email);
      // Auto-send verification code if user was redirected from login
      if (location.state?.message && location.state.message.includes('verify your email')) {
        handleAutoResend(location.state.email);
      }
    }
  }, [location.state]);

  const handleAutoResend = async (userEmail) => {
    try {
      const response = await fetch(`${getBackendUrl()}/api/auth/resend-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: userEmail }),
      });

      await response.json();
      if (response.ok) {
        setSuccess('A new verification code has been sent to your email.');
      }
    } catch (error) {
      // Silent fail for auto-resend - user can manually resend if needed
    }
  };

  const handleVerifyEmail = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${getBackendUrl()}/api/auth/verify-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          verificationCode,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(data.message);
        
        // Store token and user data
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        
        // Redirect to appropriate dashboard
        setTimeout(() => {
          if (data.user.role === 'professor') {
            navigate('/professor-dashboard');
          } else if (data.user.role === 'student') {
            navigate('/student-dashboard');
          } else {
            navigate('/admin-dashboard');
          }
        }, 2000);
      } else {
        setError(data.message);
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${getBackendUrl()}/api/auth/resend-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(data.message);
        setStep('verify');
      } else {
        setError(data.message);
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="email-verification-container">
      <div className="email-verification-card">
        <div className="verification-header">
          <div style={{ 
            fontSize: '2.5rem', 
            fontWeight: 'bold', 
            color: '#061941', 
            marginBottom: '10px',
            letterSpacing: '2px'
          }}>
            NeuHawk
          </div>
          <h1>Email Verification</h1>
          <p>Please verify your email address to complete your registration</p>
        </div>

        {step === 'verify' ? (
          <form onSubmit={handleVerifyEmail} className="verification-form">
            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email address"
                required
                disabled={location.state?.email} // Disable if email came from registration
              />
            </div>

            <div className="form-group">
              <label htmlFor="verificationCode">Verification Code</label>
              <input
                type="text"
                id="verificationCode"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="Enter 6-digit code"
                maxLength="6"
                required
                className="verification-code-input"
              />
              <small className="help-text">
                Enter the 6-digit code sent to your email
              </small>
            </div>

            {error && <div className="error-message">{error}</div>}
            {success && <div className="success-message">{success}</div>}

            <button 
              type="submit" 
              className="verify-button"
              disabled={loading || !email || !verificationCode}
            >
              {loading ? 'Verifying...' : '✓ Verify Email'}
            </button>

            <div className="verification-footer">
              <p>Didn't receive the code?</p>
              <button 
                type="button" 
                className="resend-link"
                onClick={() => setStep('resend')}
                disabled={loading}
              >
                Resend verification code
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={handleResendCode} className="resend-form">
            <div className="form-group">
              <label htmlFor="resendEmail">Email Address</label>
              <input
                type="email"
                id="resendEmail"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email address"
                required
              />
            </div>

            {error && <div className="error-message">{error}</div>}
            {success && <div className="success-message">{success}</div>}

            <button 
              type="submit" 
              className="resend-button"
              disabled={loading || !email}
            >
              {loading ? 'Sending...' : '📧 Send New Code'}
            </button>

            <div className="verification-footer">
              <button 
                type="button" 
                className="back-link"
                onClick={() => setStep('verify')}
                disabled={loading}
              >
                Back to verification
              </button>
            </div>
          </form>
        )}

        <div className="verification-help">
          <h3>💡 Need Help?</h3>
          <ul>
            <li>Check your spam/junk folder for the verification email</li>
            <li>Make sure you entered the correct email address</li>
            <li>Verification codes expire after 15 minutes for security</li>
            <li>Contact our support team if you continue to have issues</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default EmailVerification;